/**
 * App.js - Progress Tracker Frontend
 * 
 * @version 1.3.1
 * @date 2025-12-09
 * 
 * CHANGELOG:
 * v1.3.1 (2025-12-09)
 *   - Increased "Entries Waiting to Post" limit from 5 to 100
 * 
 * v1.3.0 (2025-12-09)
 *   - Group phases by first 4 characters (1000-8999)
 *   - Phases 0000-0999 remain ungrouped
 *   - Phases 9000-9999 (900000-999999) excluded entirely
 *   - Phases starting with A-Z (A00000+) remain ungrouped
 *   - Collapsible phase groups for easier navigation
 * 
 * v1.2.0 (2025-12-09)
 *   - Removed Delete Permissions info panel
 * 
 * v1.1.0 (2025-12-09)
 *   - Made MSAL redirect URI configurable via REACT_APP_REDIRECT_URI
 *   - Supports local development with localhost:3000
 * 
 * v1.0.0 (2025-12-09)
 *   - Initial versioned release
 *   - Job selection and phase progress entry
 *   - Line-item delete for submitted entries (trash icon per phase)
 *   - Permission-aware UI (canDelete flag from API)
 *   - Microsoft Entra ID authentication via MSAL
 *   - Environment-based API URL configuration
 */

import React, { useState, useEffect } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { Save, ChevronDown, ChevronUp, Calendar, Briefcase, LogOut, User, Trash2, Loader2 } from 'lucide-react';

// MSAL Configuration - uses environment variables for flexibility
const msalConfig = {
  auth: {
    clientId: 'f30bed82-596b-47d0-8765-3c172bc52f4f',
    authority: 'https://login.microsoftonline.com/26be07db-0c5e-4263-80c9-84875c23d04f',
    redirectUri: process.env.REACT_APP_REDIRECT_URI || 'https://production.southlandconcrete.com',
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  }
};

const loginRequest = {
  scopes: ['api://f30bed82-596b-47d0-8765-3c172bc52f4f/user_impersonation']
};

// Initialize MSAL
const msalInstance = new PublicClientApplication(msalConfig);

export default function ProgressTracker() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);

  // API Configuration - uses environment variable, falls back to production
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://productionapi.southlandconcrete.com/api';
  
  const [jobs, setJobs] = useState([]);
  const [phases, setPhases] = useState({});

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [entries, setEntries] = useState({});
  const [expandedPhases, setExpandedPhases] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [savedEntries, setSavedEntries] = useState([]);
  const [deletingEntry, setDeletingEntry] = useState(null); // tracks "batchId-phaseCode" being deleted

  // Helper function to group phases by first 4 characters
  // Excludes phases 900000-999999 entirely
  // Phases 0000-0999 and A00000+ remain ungrouped
  const groupPhasesByPrefix = (phaseList) => {
    const groups = {};
    const ungroupedPhases = [];
    
    phaseList.forEach(phase => {
      const phaseCode = phase.phase || '';
      const firstChar = phaseCode.charAt(0);
      const prefix = phaseCode.substring(0, 4);
      const prefixNum = parseInt(prefix, 10);
      
      // Exclude phases 9000-9999 (900000-999999)
      if (!isNaN(prefixNum) && prefixNum >= 9000 && prefixNum <= 9999) {
        return; // Skip this phase entirely
      }
      
      // Check if starts with letter (A00000+)
      const startsWithLetter = /^[A-Za-z]/.test(firstChar);
      
      // Don't group if starts with letter or first 4 digits are 0000-0999
      const isLowRange = !isNaN(prefixNum) && prefixNum < 1000;
      
      if (startsWithLetter || isLowRange) {
        ungroupedPhases.push({
          key: phase.id,
          phases: [phase],
          isGroup: false,
          sortKey: phaseCode
        });
      } else {
        // Group by first 4 characters
        if (!groups[prefix]) {
          const nameParts = phase.name.split('-');
          const groupName = nameParts[0].trim();
          groups[prefix] = {
            key: prefix,
            name: groupName,
            phases: [],
            isGroup: true,
            sortKey: prefix
          };
        }
        groups[prefix].phases.push(phase);
      }
    });
    
    // Combine and sort
    const allItems = [...ungroupedPhases, ...Object.values(groups)];
    return allItems.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  };

  const toggleGroup = (jobId, groupKey) => {
    const key = `${jobId}|||${groupKey}`;
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Set document title
  useEffect(() => {
    document.title = 'Progress Tracker';
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    msalInstance.initialize().then(() => {
      msalInstance.handleRedirectPromise().then((response) => {
        if (response) {
          setUser(response.account);
          setIsAuthenticated(true);
          getAccessToken(response.account);
        } else {
          checkAuthStatus();
        }
      }).catch(error => {
        console.error('Redirect error:', error);
        setLoading(false);
      });
    });
  }, []);

  const checkAuthStatus = async () => {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        setUser(accounts[0]);
        setIsAuthenticated(true);
        await getAccessToken(accounts[0]);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      alert('Login failed: ' + error.message);
      setLoading(false);
    }
  };

  const getAccessToken = async (account) => {
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: account || msalInstance.getAllAccounts()[0]
      });
      setAccessToken(response.accessToken);
      setLoading(false);
      return response.accessToken;
    } catch (error) {
      console.error('Token acquisition failed:', error);
      try {
        await msalInstance.acquireTokenRedirect(loginRequest);
      } catch (interactiveError) {
        console.error('Interactive token acquisition failed:', interactiveError);
        setLoading(false);
        return null;
      }
    }
  };

  const handleLogout = async () => {
    try {
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Load jobs from API when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadJobs();
    }
  }, [isAuthenticated]);

  const loadJobs = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/jobs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      } else {
        console.error('Failed to load jobs');
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const loadPhases = async (jobId) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/phases`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPhases(prev => ({
          ...prev,
          [jobId]: data
        }));
      }
    } catch (error) {
      console.error('Error loading phases:', error);
    }
  };

  const handleJobSelect = (job) => {
    // Toggle: if clicking the same job, collapse it
    if (selectedJob?.id === job.id) {
      setSelectedJob(null);
      // DON'T clear entries - keep the data
      setExpandedPhases({});
    } else {
      // Select new job
      setSelectedJob(job);
      setExpandedPhases({});
      
      if (!phases[job.id]) {
        loadPhases(job.id);
      }
    }
  };

  const handleUnitChange = (jobId, phaseId, value) => {
    const key = `${jobId}|||${phaseId}`;
    setEntries(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const togglePhase = (jobId, phaseId) => {
    const key = `${jobId}|||${phaseId}`;
    setExpandedPhases(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    try {
      const token = await getAccessToken();
      
      // Get the user email from MSAL account object
      const userEmail = user.username || user.preferred_username || user.email || 'unknown@company.com';
      
      console.log('Saving with user email:', userEmail);
      
      // Group entries by job
      const entriesByJob = {};
      Object.entries(entries).forEach(([key, units]) => {
        if (parseFloat(units) > 0) {
          // Split the key back into jobId and phaseId
          const [jobId, phaseId] = key.split('|||');
          if (!entriesByJob[jobId]) {
            entriesByJob[jobId] = {};
          }
          entriesByJob[jobId][phaseId] = units;
        }
      });
      
      console.log('Entries grouped by job:', entriesByJob);
      
      if (Object.keys(entriesByJob).length === 0) {
        alert('No entries to save');
        return;
      }
      
      let successCount = 0;
      let failCount = 0;
      const results = [];
      
      // Save each job separately
      for (const [jobId, jobEntries] of Object.entries(entriesByJob)) {
        const progressData = {
          jobId: jobId,
          date: selectedDate,
          entries: jobEntries,
          userEmail: userEmail
        };

        try {
          const response = await fetch(`${API_BASE_URL}/progress`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(progressData)
          });

          if (response.ok) {
            const result = await response.json();
            results.push({
              jobId,
              success: true,
              batchId: result.batchId,
              reused: result.batchReused
            });
            successCount++;
          } else {
            const error = await response.json();
            results.push({
              jobId,
              success: false,
              error: error.error
            });
            failCount++;
          }
        } catch (error) {
          console.error('Error saving job', jobId, ':', error);
          results.push({
            jobId,
            success: false,
            error: error.message
          });
          failCount++;
        }
      }
      
      // Show results
      if (successCount > 0 && failCount === 0) {
        const batchInfo = results.map(r => `Job ${r.jobId}: Batch ${r.batchId}${r.reused ? ' (reused)' : ''}`).join('\n');
        alert(`Successfully saved ${successCount} job(s):\n${batchInfo}`);
        setEntries({});
        
        // Refresh phases for all jobs that were saved
        for (const jobId of Object.keys(entriesByJob)) {
          await loadPhases(jobId);
        }
        
        // Reload recent entries
        await loadRecentEntries();
      } else if (successCount > 0 && failCount > 0) {
        alert(`Saved ${successCount} job(s) successfully, but ${failCount} failed. Check console for details.`);
        console.error('Failed saves:', results.filter(r => !r.success));
      } else {
        alert('Failed to save progress. Check console for details.');
        console.error('All saves failed:', results);
      }
    } catch (error) {
      console.error('Error saving progress:', error);
      alert('Error saving progress. Check console for details.');
    }
  };

  const loadRecentEntries = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/progress?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSavedEntries(data);
      }
    } catch (error) {
      console.error('Error loading recent entries:', error);
    }
  };

  const handleDelete = async (entry, phaseCode, units) => {
    if (!window.confirm(`Are you sure you want to delete this entry?\n\nPhase: ${phaseCode}\nUnits: ${Math.round(units)}\nJob: ${entry.jobName}\nDate: ${new Date(entry.date).toLocaleDateString()}\n\nThis action cannot be undone!`)) {
      return;
    }
    
    const deleteKey = `${entry.batchId}-${phaseCode}`;
    setDeletingEntry(deleteKey);
    
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/progress/entry`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          batchId: entry.batchId,
          jobId: entry.jobId,
          phaseCode: phaseCode,
          actualDate: entry.date,
          month: entry.month
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Successfully deleted ${phaseCode} (${Math.round(result.deletedUnits)} units)`);
        // Reload the entries list
        await loadRecentEntries();
        // Refresh phases for the affected job
        await loadPhases(entry.jobId);
      } else {
        const error = await response.json();
        alert(`Failed to delete entry: ${error.message || error.error}`);
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry. Check console for details.');
    } finally {
      setDeletingEntry(null);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadRecentEntries();
    }
  }, [isAuthenticated]);

  const getTotalUnits = () => {
    return Object.values(entries).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="bg-indigo-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-10 h-10 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Progress Tracker</h1>
            <p className="text-gray-600">Sign in with your Microsoft account</p>
          </div>
          
          <button
            onClick={handleLogin}
            className="w-full bg-indigo-600 text-white py-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
          >
            <User className="w-5 h-5" />
            Sign in with Microsoft
          </button>
          
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Secure authentication via Microsoft Entra ID</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-indigo-600 text-white p-4 shadow-lg">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Daily Progress Tracker</h1>
            <p className="text-indigo-100 text-sm mt-1">Vista Database Sync Ready</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="font-semibold">{user.name}</div>
              <div className="text-sm text-indigo-200">{user.username}</div>
            </div>
            <button
              onClick={handleLogout}
              className="bg-indigo-700 hover:bg-indigo-800 p-2 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2">
            <Calendar className="w-5 h-5 text-indigo-600" />
            Entry Date
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <label className="flex items-center gap-2 text-gray-700 font-semibold mb-3">
            <Briefcase className="w-5 h-5 text-indigo-600" />
            Select Job & Enter Progress
          </label>
          <div className="grid grid-cols-1 gap-2">
            {jobs.map(job => (
              <div key={job.id} className="border-2 rounded-lg overflow-hidden transition-all" style={{
                borderColor: selectedJob?.id === job.id ? '#4f46e5' : '#e5e7eb'
              }}>
                <button
                  onClick={() => handleJobSelect(job)}
                  className={`w-full p-4 text-left transition-all ${
                    selectedJob?.id === job.id
                      ? 'bg-indigo-50'
                      : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold text-gray-900">{job.name}</div>
                  <div className="text-sm text-gray-500">{job.number}</div>
                </button>
                
                {selectedJob?.id === job.id && phases[job.id] && (
                  <div className="border-t-2 border-indigo-100 bg-gray-50 p-4">
                    <h3 className="font-semibold text-gray-700 mb-3">Phase Progress</h3>
                    <div className="space-y-2">
                      {groupPhasesByPrefix(phases[job.id]).map(group => {
                        const groupExpandKey = `${job.id}|||group-${group.key}`;
                        const isGroupExpanded = expandedGroups[groupExpandKey];
                        
                        // For ungrouped single phases, render directly
                        if (!group.isGroup) {
                          const phase = group.phases[0];
                          const entryKey = `${job.id}|||${phase.id}`;
                          const expandKey = `${job.id}|||${phase.id}`;
                          return (
                            <div key={phase.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                              <button
                                onClick={() => togglePhase(job.id, phase.id)}
                                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                              >
                                <span className="font-medium text-gray-700 text-sm">{phase.name}</span>
                                <div className="flex items-center gap-3">
                                  {entries[entryKey] && (
                                    <span className="text-indigo-600 font-bold text-sm">{entries[entryKey]} {phase.unitType}</span>
                                  )}
                                  {expandedPhases[expandKey] ? (
                                    <ChevronUp className="w-4 h-4 text-gray-500" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                  )}
                                </div>
                              </button>
                              {expandedPhases[expandKey] && (
                                <div className="p-3 bg-gray-50 border-t border-gray-200">
                                  <div className="grid grid-cols-4 gap-2 mb-3">
                                    <div className="text-center p-2 bg-blue-50 rounded border border-blue-200">
                                      <div className="text-xs text-gray-600 font-medium">Estimated</div>
                                      <div className="text-lg font-bold text-blue-700">{Math.round(phase.estimatedUnits)}</div>
                                      <div className="text-xs text-gray-500">{phase.unitType}</div>
                                    </div>
                                    <div className="text-center p-2 bg-green-50 rounded border border-green-200">
                                      <div className="text-xs text-gray-600 font-medium">Posted</div>
                                      <div className="text-lg font-bold text-green-700">{Math.round(phase.postedUnits)}</div>
                                      <div className="text-xs text-gray-500">{phase.unitType}</div>
                                    </div>
                                    <div className="text-center p-2 bg-yellow-50 rounded border border-yellow-200">
                                      <div className="text-xs text-gray-600 font-medium">Submitted</div>
                                      <div className="text-lg font-bold text-yellow-700">{Math.round(phase.submittedUnits)}</div>
                                      <div className="text-xs text-gray-500">{phase.unitType}</div>
                                    </div>
                                    <div className="text-center p-2 bg-purple-50 rounded border border-purple-200">
                                      <div className="text-xs text-gray-600 font-medium">Remaining</div>
                                      <div className={`text-lg font-bold ${
                                        (phase.estimatedUnits - phase.postedUnits - phase.submittedUnits) >= 0 
                                          ? 'text-purple-700' 
                                          : 'text-red-700'
                                      }`}>
                                        {Math.round(phase.estimatedUnits - phase.postedUnits - phase.submittedUnits)}
                                      </div>
                                      <div className="text-xs text-gray-500">{phase.unitType}</div>
                                    </div>
                                  </div>
                                  <label className="block text-xs font-medium text-gray-600 mb-2">
                                    Units Completed Today ({phase.unitType})
                                  </label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Enter units"
                                    value={entries[entryKey] || ''}
                                    onChange={(e) => handleUnitChange(job.id, phase.id, e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                                  />
                                </div>
                              )}
                            </div>
                          );
                        }
                        
                        // For grouped phases, show collapsible group header
                        const groupEntryCount = group.phases.filter(p => entries[`${job.id}|||${p.id}`]).length;
                        
                        return (
                          <div key={group.key} className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                            {/* Group Header */}
                            <button
                              onClick={() => toggleGroup(job.id, `group-${group.key}`)}
                              className="w-full flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                              <span className="font-semibold text-gray-800">{group.name}</span>
                              <div className="flex items-center gap-3">
                                {groupEntryCount > 0 && (
                                  <span className="text-indigo-600 font-bold text-sm">{groupEntryCount} entries</span>
                                )}
                                <span className="text-xs text-gray-500">({group.phases.length} phases)</span>
                                {isGroupExpanded ? (
                                  <ChevronUp className="w-5 h-5 text-gray-600" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-600" />
                                )}
                              </div>
                            </button>
                            
                            {/* Phases within group */}
                            {isGroupExpanded && (
                              <div className="border-t border-gray-300">
                                {group.phases.map(phase => {
                                  const entryKey = `${job.id}|||${phase.id}`;
                                  const expandKey = `${job.id}|||${phase.id}`;
                                  return (
                                    <div key={phase.id} className="border-b border-gray-200 last:border-b-0">
                                      <button
                                        onClick={() => togglePhase(job.id, phase.id)}
                                        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                                      >
                                        <span className="font-medium text-gray-700 text-sm">{phase.name}</span>
                                        <div className="flex items-center gap-3">
                                          {entries[entryKey] && (
                                            <span className="text-indigo-600 font-bold text-sm">{entries[entryKey]} {phase.unitType}</span>
                                          )}
                                          {expandedPhases[expandKey] ? (
                                            <ChevronUp className="w-4 h-4 text-gray-500" />
                                          ) : (
                                            <ChevronDown className="w-4 h-4 text-gray-500" />
                                          )}
                                        </div>
                                      </button>
                                      {expandedPhases[expandKey] && (
                                        <div className="p-3 bg-gray-50 border-t border-gray-200">
                                          <div className="grid grid-cols-4 gap-2 mb-3">
                                            <div className="text-center p-2 bg-blue-50 rounded border border-blue-200">
                                              <div className="text-xs text-gray-600 font-medium">Estimated</div>
                                              <div className="text-lg font-bold text-blue-700">{Math.round(phase.estimatedUnits)}</div>
                                              <div className="text-xs text-gray-500">{phase.unitType}</div>
                                            </div>
                                            <div className="text-center p-2 bg-green-50 rounded border border-green-200">
                                              <div className="text-xs text-gray-600 font-medium">Posted</div>
                                              <div className="text-lg font-bold text-green-700">{Math.round(phase.postedUnits)}</div>
                                              <div className="text-xs text-gray-500">{phase.unitType}</div>
                                            </div>
                                            <div className="text-center p-2 bg-yellow-50 rounded border border-yellow-200">
                                              <div className="text-xs text-gray-600 font-medium">Submitted</div>
                                              <div className="text-lg font-bold text-yellow-700">{Math.round(phase.submittedUnits)}</div>
                                              <div className="text-xs text-gray-500">{phase.unitType}</div>
                                            </div>
                                            <div className="text-center p-2 bg-purple-50 rounded border border-purple-200">
                                              <div className="text-xs text-gray-600 font-medium">Remaining</div>
                                              <div className={`text-lg font-bold ${
                                                (phase.estimatedUnits - phase.postedUnits - phase.submittedUnits) >= 0 
                                                  ? 'text-purple-700' 
                                                  : 'text-red-700'
                                              }`}>
                                                {Math.round(phase.estimatedUnits - phase.postedUnits - phase.submittedUnits)}
                                              </div>
                                              <div className="text-xs text-gray-500">{phase.unitType}</div>
                                            </div>
                                          </div>
                                          <label className="block text-xs font-medium text-gray-600 mb-2">
                                            Units Completed Today ({phase.unitType})
                                          </label>
                                          <input
                                            type="number"
                                            step="0.01"
                                            placeholder="Enter units"
                                            value={entries[entryKey] || ''}
                                            onChange={(e) => handleUnitChange(job.id, phase.id, e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-4 p-3 bg-indigo-50 rounded-lg border-2 border-indigo-200">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-700">Total Units Today:</span>
                        <span className="text-xl font-bold text-indigo-600">{getTotalUnits().toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="mt-3 pb-20">
                      {/* Spacer to prevent content from being hidden behind fixed button */}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Fixed Save Button - Floats at bottom when there are entries */}
        {getTotalUnits() > 0 && (
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-gray-100 to-transparent pointer-events-none">
            <div className="max-w-4xl mx-auto pointer-events-auto">
              <button
                onClick={handleSave}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white py-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors shadow-xl"
              >
                <Save className="w-5 h-5" />
                Save Progress Entry ({getTotalUnits().toFixed(2)} units)
              </button>
            </div>
          </div>
        )}

        {savedEntries.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Entries Waiting to Post</h2>
            
            <div className="space-y-3">
              {savedEntries.map(entry => (
                <div key={entry.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold text-gray-800">{entry.jobName}</div>
                      <div className="text-sm text-gray-500">
                        {(() => {
                          if (!entry.date) return 'No date';
                          const dateStr = entry.date.toString();
                          if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
                            const [year, month, day] = dateStr.split('T')[0].split('-');
                            return `${month}/${day}/${year}`;
                          }
                          try {
                            return new Date(entry.date).toLocaleDateString('en-US', { 
                              month: '2-digit', 
                              day: '2-digit', 
                              year: 'numeric',
                              timeZone: 'UTC'
                            });
                          } catch (e) {
                            return dateStr;
                          }
                        })()}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Batch {entry.batchId} • By: {entry.user || entry.createdBy}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-indigo-600">
                        {Math.round(entry.totalUnits)}
                      </div>
                      <div className="text-xs text-gray-500">total units</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">
                    {Object.entries(entry.entries).map(([phase, units]) => {
                      const deleteKey = `${entry.batchId}-${phase}`;
                      const isDeleting = deletingEntry === deleteKey;
                      return (
                        <div key={phase} className="flex justify-between items-center py-1">
                          <span className="flex-1">{phase}</span>
                          <span className="font-medium mr-3">{Math.round(units)}</span>
                          {entry.canDelete && (
                            <button
                              onClick={() => handleDelete(entry, phase, units)}
                              disabled={isDeleting}
                              className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title={`Delete ${phase}`}
                            >
                              {isDeleting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}